import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { apiClient, foldIntoPrevious } from '@/api/client';
import { useNotification } from '@/contexts/NotificationContext';
import { isValidTimeRange, generateTimeSlots, getStartHour, hhmmToMinutes, hmmmToSlot, hmmmToEndSlot, getDurationPalette, formatCounterLabel, getBreakOptions, minutesSpan } from '@/config/modeConfig';
import {
  getSOSExampleText,
  OT_PRESETS_MORNING,
  OT_PRESETS_NIGHT,
  type OTPreset,
} from '@/config/examples';
import { computeAssignedTiming, slotToHhmm, timingSpan } from '@/utils/partialTiming';
import { useMaxCounterDuration } from '@/utils/maxCounterDuration';
import type { OfficerMetadata, OperationMessage, OperationResult, RosterState, OTBreakStarts, OTPresetsConfig, PartiallyAssignedOfficer, SkippedOfficerInfo, SOSOperationRow } from '@/types/api';
import { OTBreakTimes, OTHotoStrip } from './OTBreakTimes';
import { breakStartsPayload, defaultBreakStarts, noBreakTimeTicked } from '@/utils/otBreakStarts';
import { BreakSizeSelects } from '@/components/BreakSizeSelects';
import { opensShift, useUntickShiftStartSos } from '@/utils/sosPasteDefaults';

interface AddOfficersTabProps {
  generated: boolean;
  /** Called after a successful add so the parent can refresh; the mutation's own snapshot
   *  is passed through so the grid can be patched instantly instead of waiting on a refetch. */
  onAdded?: (result?: Pick<OperationResult, 'snapshot'>) => void;
  /** Called with the full roster state an undo returned (cancelling an add), so the
   *  parent can apply it directly — the server sends this tab no separate echo of it. */
  onStateRestored?: (state: RosterState) => void;
  onSwitchTab?: (tab: string) => void;
  subTab: 'main' | 'sos' | 'ot';
  onSubTabChange: (sub: 'main' | 'sos' | 'ot') => void;
  mode?: 'arrival' | 'departure';
  shift?: 'morning' | 'night';
  onOperationMessage?: (msg: OperationMessage) => void;
  /** Existing roster officers — used to flag already-added / same-name-elsewhere on paste. */
  officerMetadata?: Record<string, OfficerMetadata>;
}

function fmtTime(t: string) { return `${t.slice(0, 2)}:${t.slice(2)}`; }

// ── Paste duplicate detection ───────────────────────────────────────────────
// 'duplicate': same name + same deployment + overlapping window — almost certainly
//   a re-paste of a group already in the roster.
// 'extend': same name + same deployment but a DIFFERENT (non-overlapping) window —
//   probably the same posting with updated/extended hours rather than a fresh
//   deployment, so it's called out distinctly from 'elsewhere' with the officer's
//   existing window(s) shown, but still left selected (not auto-blocked) since it
//   could legitimately be a second window.
// 'elsewhere': same name but a DIFFERENT deployment — the weakest signal (could be
//   a coincidental name match), so only a generic warning, no window detail.
type DupStatus = 'new' | 'duplicate' | 'extend' | 'elsewhere';

interface ExistingWindow {
  text: string; // "HHMM-HHMM"
  /** Officer key of the existing roster entry this window belongs to (e.g. "S3") —
   * lets the supervisor identify exactly which entry is being referred to. */
  officerKey: string;
  /** True when the pasted timing segment overlapping this window has a different
   * start/end than what's on file — flagged so the differing HHMM can be shown in red
   * instead of requiring the user to manually diff the two ranges. Only ever true for
   * 'duplicate' status: 'extend' windows never overlap a pasted segment, so nothing to compare. */
  startDiffers: boolean;
  endDiffers: boolean;
}

interface DupCheck {
  status: DupStatus;
  /** Existing windows already on file for this name+deployment, de-duplicated. */
  existingWindows: ExistingWindow[];
}

function normalizeForMatch(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function timeRangesOverlap(aSt: string, aEt: string, bSt: string, bEt: string): boolean {
  if (!aSt || !aEt || !bSt || !bEt) return false;
  const a1 = hhmmToMinutes(aSt), a2 = hhmmToMinutes(aEt);
  const b1 = hhmmToMinutes(bSt), b2 = hhmmToMinutes(bEt);
  return a1 < b2 && b1 < a2;
}

function checkDuplicate(
  officerMetadata: Record<string, OfficerMetadata> | undefined,
  deployment: string,
  timingSegments: Array<{ st: string; et: string }>,
  officerName: string,
): DupCheck {
  const name = normalizeForMatch(officerName);
  if (!name || !officerMetadata) return { status: 'new', existingWindows: [] };
  const dep = normalizeForMatch(deployment);

  let sameNameFound = false;
  let sameDeploymentFound = false;
  let overlapsExisting = false;
  const rangeSet = new Map<string, { start: string; end: string; officerKey: string }>();

  for (const [officerKey, meta] of Object.entries(officerMetadata)) {
    if (normalizeForMatch(meta.name || '') !== name) continue;
    sameNameFound = true;
    if (normalizeForMatch(meta.deployment || '') !== dep) continue;
    sameDeploymentFound = true;

    const existingRanges = meta.availability_segments && meta.availability_segments.length > 0
      ? meta.availability_segments
      : [{ start: meta.availability_start ?? '', end: meta.availability_end ?? '' }];
    for (const r of existingRanges) {
      if (!r.start || !r.end) continue;
      rangeSet.set(`${officerKey}|${r.start}-${r.end}`, { ...r, officerKey });
      if (timingSegments.some(seg => timeRangesOverlap(seg.st, seg.et, r.start, r.end))) {
        overlapsExisting = true;
      }
    }
  }

  // For each existing window, find a pasted segment that overlaps it (if any) and
  // compare exact start/end — flags which HHMM differs so it can be shown in red.
  const existingWindows: ExistingWindow[] = Array.from(rangeSet.values())
    .sort((a, b) => `${a.start}-${a.end}`.localeCompare(`${b.start}-${b.end}`))
    .map(r => {
      const overlappingSeg = timingSegments.find(seg => timeRangesOverlap(seg.st, seg.et, r.start, r.end));
      return {
        text: `${r.start}-${r.end}`,
        officerKey: r.officerKey,
        startDiffers: !!overlappingSeg && overlappingSeg.st !== r.start,
        endDiffers: !!overlappingSeg && overlappingSeg.et !== r.end,
      };
    });

  if (sameDeploymentFound && overlapsExisting) return { status: 'duplicate', existingWindows };
  if (sameDeploymentFound) return { status: 'extend', existingWindows };
  if (sameNameFound) return { status: 'elsewhere', existingWindows: [] };
  return { status: 'new', existingWindows: [] };
}

/** Renders "HHMM-HHMM, HHMM-HHMM" with any differing start/end shown in red, so the
 * user doesn't have to manually compare the existing window against what was just pasted. */
function WindowsWithDiff({ windows }: { windows: ExistingWindow[] }) {
  return (
    <>
      {windows.map((w, i) => {
        const [start, end] = w.text.split('-');
        return (
          <span key={w.text}>
            {i > 0 && ', '}
            <span style={w.startDiffers ? { color: '#dc2626', fontWeight: 800 } : undefined}>{start}</span>
            -
            <span style={w.endDiffers ? { color: '#dc2626', fontWeight: 800 } : undefined}>{end}</span>
          </span>
        );
      })}
    </>
  );
}

/** Builds the Inbox remarks text for a flagged officer: one line per matching existing
 * roster entry, naming its officer key so the supervisor can find the exact row it
 * refers to — e.g. "S3: Armourer - Isbandi already existed 1300-1630". */
function formatDuplicateNote(windows: ExistingWindow[], deployment: string, name: string): string {
  return windows
    .map(w => `${w.officerKey}: ${deployment} - ${name} already existed ${w.text}`)
    .join('; ');
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface SOSOfficerItem {
  id: number;
  name: string;
  selected: boolean;
}
interface SOSGroup {
  id: number;
  deployment: string;
  /** One or more timing windows — each must have valid st and et to be submitted. */
  timingSegments: Array<{ st: string; et: string }>;
  expanded: boolean;
  officers: SOSOfficerItem[];
  /** Break sizes for the whole group: [1st, 2nd, 3rd] slots. null = backend default. */
  breakSizes: (number | null)[];
  /** Last pattern fetched from /break-pattern — used to detect user overrides. */
  breakDefaults: (number | null)[];
  /** True when the user has explicitly changed a break size from the backend default. */
  breakModified: boolean;
}
// ── Availability window duration pill ─────────────────────────────────────────
// Palette: steps alternate border-only change then fill change every 4 slots.
// Step n → fill = AVAIL_COLORS[n>>1], border = AVAIL_COLORS[(n+1)>>1]
const AVAIL_COLORS  = ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#7c3aed', '#4c1d95'];
const AVAIL_TEXT    = ['#1e3a5f', '#1e3a5f', 'white',   'white',   'white',   'white'];

function availPill(slotCount: number): { bg: string; border: string; color: string } {
  if (slotCount <= 0) return { bg: 'white', border: '#d1d5db', color: '#6b7280' };
  const n    = Math.min(Math.floor((slotCount - 1) / 4), (AVAIL_COLORS.length - 1) * 2);
  const fi   = Math.min(n >> 1, AVAIL_COLORS.length - 1);
  const bi   = Math.min((n + 1) >> 1, AVAIL_COLORS.length - 1);
  return { bg: AVAIL_COLORS[fi], border: AVAIL_COLORS[bi], color: AVAIL_TEXT[fi] };
}

// ── Break size options (shared source: modeConfig.getBreakOptions) ─────────────
const BREAK_OPTIONS = getBreakOptions();

/**
 * Clip template counter rows to a custom availability window — drops rows entirely
 * outside it and shortens rows that only partially overlap it.
 */
function trimRowsToAvailability(
  rows: Array<{ start: string; end: string; counter: number }>,
  availStart: string,
  availEnd: string,
  startHour: number,
): Array<{ start: string; end: string; counter: number }> {
  const winStart = hmmmToSlot(availStart, startHour);
  const winEnd = hmmmToEndSlot(availEnd, startHour);
  const out: Array<{ start: string; end: string; counter: number }> = [];
  for (const r of rows) {
    const s = Math.max(hmmmToSlot(r.start, startHour), winStart);
    const e = Math.min(hmmmToEndSlot(r.end, startHour), winEnd);
    if (e > s) {
      out.push({
        start: s === hmmmToSlot(r.start, startHour) ? r.start : slotToHhmm(s, startHour),
        end: e === hmmmToEndSlot(r.end, startHour) ? r.end : slotToHhmm(e, startHour),
        counter: r.counter,
      });
    }
  }
  return out;
}

// ── Tab button colors ──────────────────────────────────────────────────────────
const TAB_ACTIVE: Record<'main' | 'sos' | 'ot', React.CSSProperties> = {
  main: { background: '#e0f2fe', color: '#0369a1', borderColor: '#7dd3fc' },
  sos:  { background: '#ffe4ee', color: '#9d174d', borderColor: '#ffc6d9' },
  ot:   { background: '#fef9c3', color: '#78350f', borderColor: '#fde68a' },
};
// Confirm buttons are the primary call-to-action for their tab — filled with the same
// pastel shade as that officer type's cell fill on the roster grid (see color legend).
const CONFIRM_STYLE: Record<'main' | 'sos' | 'ot', React.CSSProperties> = {
  main: { background: '#a2d2ff', border: '1px solid #a2d2ff', color: '#1e40af' },
  sos:  { background: '#ffc6d9', border: '1px solid #ffc6d9', color: '#9d174d' },
  ot:   { background: '#FDFD96', border: '1px solid #FDFD96', color: '#713f12' },
};

export function AddOfficersTab({
  generated,
  onAdded,
  onStateRestored,
  onSwitchTab,
  subTab,
  onSubTabChange,
  mode,
  shift,
  onOperationMessage,
  officerMetadata,
}: AddOfficersTabProps) {
  const { notifyInfo, notifySuccess, notifyWarning, notifyError, dismissSpinner } = useNotification();

  // 15-min time slots covering the full shift window (morning: 10:00–22:00, night: 22:00–10:00)
  const TIME_SLOTS_15 = generateTimeSlots(shift ?? 'morning');
  const untickShiftStart = useUntickShiftStartSos();
  // The hour this shift's slot 0 sits at — every HHMM here is read relative to it,
  // so a night shift's 0100 is after its 2200 rather than fourteen hours before.
  const shiftStartHour = getStartHour(shift ?? 'morning');

  const exampleText = getSOSExampleText(mode ?? 'arrival', shift ?? 'morning');
  const [exampleCopied, setExampleCopied] = useState(false);

  // ── SOS state ────────────────────────────────────────────────────────────────
  const [sosRawText, setSOSRawText] = useState('');
  const [sosGroups, setSosGroups] = useState<SOSGroup[]>([]);
  // The default break rules start just past the max ctr duration, so a change to it
  // re-fetches every default break shown here (ones the user edited are kept).
  const [maxCounterDuration] = useMaxCounterDuration();
  // Set when Extract runs on non-empty text but finds no officers — surfaces the
  // example format (hidden the rest of the time) so the user can see what went wrong.
  const [sosExtractFailed, setSosExtractFailed] = useState(false);
  // True right after Extract is pressed on an empty textarea — turns the
  // textarea red with a "nothing to extract" note below it. Cleared as soon
  // as the user types anything, distinct from sosExtractFailed (pasted text
  // that didn't parse into any officers).
  const [sosExtractEmptyPressed, setSosExtractEmptyPressed] = useState(false);
  const [sosLoading, setSOSLoading] = useState(false);
  const [sosAutoAssign, setSosAutoAssign] = useState(true);
  const [partialOfficers, setPartialOfficers] = useState<PartiallyAssignedOfficer[]>([]);
  const [failedOfficers, setFailedOfficers] = useState<SkippedOfficerInfo[]>([]);
  // Rows for officers from the same batch that were fully resolved (added/skipped/dup-flagged)
  // when a partial/failed dialog is about to be shown — stashed here so the eventual keep/remove
  // decision can fold them into ONE Inbox message covering the whole action, instead of one
  // message from the initial confirm and a second one from the decision.
  const [pendingCleanRows, setPendingCleanRows] = useState<SOSOperationRow[]>([]);
  // Second page of the partial dialog: confirm break slots before re-assigning
  const [partialKeep, setPartialKeep] = useState<Map<string, boolean>>(new Map());
  // The window each partial officer ends up with if kept, as the backend's dry run settled it —
  // what the dialog shows, what its default breaks are for, and what is sent back.
  const [partialProposed, setPartialProposed] = useState<Map<string, string>>(new Map());
  const [partialBreakSizes, setPartialBreakSizes] = useState<Map<string, (number | null)[]>>(new Map());
  // The dialog's Confirm is in flight (removing skipped officers, re-assigning the kept
  // ones) — shown as a spinner on the button, and a second tap is ignored.
  const [partialDeciding, setPartialDeciding] = useState(false);
  // The dialog's ✕ is in flight — it rolls the whole add back, so it must not be
  // possible to fire it twice.
  const [cancellingAdd, setCancellingAdd] = useState(false);

  // ── OT presets (admin-configurable, falls back to examples.ts hardcoded) ─────
  const [adminOTPresetsConfig, setAdminOTPresetsConfig] = useState<OTPresetsConfig | null>(null);
  useEffect(() => {
    apiClient.adminConfig.getOTPresets().then(setAdminOTPresetsConfig).catch(() => {});
  }, []);

  // ── OT state ─────────────────────────────────────────────────────────────────
  const otPresets: OTPreset[] = (() => {
    if (adminOTPresetsConfig) {
      const list = shift === 'night' ? adminOTPresetsConfig.night : adminOTPresetsConfig.morning;
      if (list && list.length > 0) return list;
    }
    return shift === 'night' ? OT_PRESETS_NIGHT : OT_PRESETS_MORNING;
  })();
  const [otPreset, setOTPreset] = useState(otPresets[0].label.split(' ')[0]);
  const [otStartTime, setOTStartTime] = useState(otPresets[0].start);
  const [otEndTime, setOTEndTime] = useState(otPresets[0].end);
  const [otNumOfficers, setOTNumOfficers] = useState(5);
  const [otLoading, setOTLoading] = useState(false);
  const [otAutoAssign, setOTAutoAssign] = useState(true);
  const [otBreakSizes, setOTBreakSizes] = useState<(number | null)[]>([null, null, null]);
  const [otBreakDefaults, setOTBreakDefaults] = useState<(number | null)[]>([null, null, null]);
  const [otBreakModified, setOTBreakModified] = useState(false);
  // When the officer leaves, if past HOTO ('' = at the window end) — breaks are counted to it.
  const [otStaysUntil, setOTStaysUntil] = useState('');
  // The break start times the backend's OT rule lists for this window, and the ones ticked.
  const [otBreakStarts, setOTBreakStarts] = useState<OTBreakStarts | null>(null);
  const [otBreakStartsChecked, setOTBreakStartsChecked] = useState<Set<string>>(new Set());
  const [otAllow3hLastBreak, setOtAllow3hLastBreak] = useState(false);

  // ── Main officer state ────────────────────────────────────────────────────────
  const [usedSns, setUsedSns] = useState<number[]>([]);
  const [mainSN, setMainSN] = useState<number>(0);
  const [mainName, setMainName] = useState('');
  const [mainLoading, setMainLoading] = useState(false);
  const [mainTemplateRows, setMainTemplateRows] = useState<Array<{ start: string; end: string; counter: number }>>([]);
  const [mainTemplateLoading, setMainTemplateLoading] = useState(false);
  const [mainAvailStart, setMainAvailStart] = useState(TIME_SLOTS_15[0]);
  const [mainAvailEnd, setMainAvailEnd] = useState(TIME_SLOTS_15[TIME_SLOTS_15.length - 1]);

  // Load used SNs whenever the main tab is active
  useEffect(() => {
    if (subTab === 'main' && generated) {
      apiClient.getUsedSns().then(r => {
        setUsedSns(r.used_sns);
      }).catch(() => {});
    }
  }, [subTab, generated]);

  // Fetch pre-assigned template when an SN is selected
  useEffect(() => {
    if (!mainSN) { setMainTemplateRows([]); return; }
    setMainTemplateLoading(true);
    apiClient.getMainOfficerTemplate(mainSN).then(r => {
      setMainTemplateRows(r.assignments);
      if (r.assignments.length > 0) {
        setMainAvailStart(r.assignments[0].start);
        setMainAvailEnd(r.assignments[r.assignments.length - 1].end);
      }
    }).catch(() => {
      setMainTemplateRows([]);
    }).finally(() => setMainTemplateLoading(false));
  }, [mainSN]);

  // Fetch OT break defaults from backend whenever timing or shift changes.
  // Always applies to both defaults and displayed sizes (timing change resets any user edit).
  useEffect(() => {
    if (!isValidTimeRange(otStartTime, otEndTime, shift ?? 'morning')) return;
    const timing = `${otStartTime}-${otEndTime}`;
    const timer = setTimeout(() => {
      apiClient.getBreakPattern({ timing, officer_type: 'ot', shift: shift ?? 'morning' })
        .then(({ pattern }) => {
          const sizes: (number | null)[] = [pattern[0] ?? null, pattern[1] ?? null, pattern[2] ?? null];
          setOTBreakDefaults(sizes);
          setOTBreakSizes(sizes);
          setOTBreakModified(false);
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [otStartTime, otEndTime, shift, maxCounterDuration]);

  // The break start checklist for this window, from the scheduler's own rule. Re-fetched
  // (and re-ticked to its defaults) whenever the window, when the officer leaves, or the
  // planned break sizes change — each moves the latest start.
  const otPlannedBreakSlots = otBreakSizes.filter((s): s is number => s !== null).join(',');
  useEffect(() => {
    if (!isValidTimeRange(otStartTime, otEndTime, shift ?? 'morning')) { setOTBreakStarts(null); return; }
    const timer = setTimeout(() => {
      apiClient.getOTBreakStarts({
        start: otStartTime, end: otEndTime, shift: shift ?? 'morning',
        stays_until: otStaysUntil || undefined,
        break_slots: otPlannedBreakSlots ? otPlannedBreakSlots.split(',').map(Number) : undefined,
      })
        .then(r => { setOTBreakStarts(r); setOTBreakStartsChecked(defaultBreakStarts(r.options)); })
        .catch(() => setOTBreakStarts(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [otStartTime, otEndTime, shift, otStaysUntil, otPlannedBreakSlots]);

  const sosGroupsRef = useRef(sosGroups);
  sosGroupsRef.current = sosGroups;
  const lastCap = useRef(maxCounterDuration);
  useEffect(() => {
    if (lastCap.current === maxCounterDuration) return;
    lastCap.current = maxCounterDuration;
    if (sosGroupsRef.current.length > 0) void fetchSOSGroupDefaults(sosGroupsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxCounterDuration]);

  const nextId = useRef(1);

  // Keep the paste-review checkboxes honest if the roster changes while this drawer is
  // still open (e.g. another supervisor adds the same officer via realtime sync) — without
  // this, a newly-duplicate officer would show the "already added" badge while its
  // checkbox stayed checked from the original paste, letting a real duplicate through.
  // Only ever auto-unchecks; never re-checks something the user deliberately deselected.
  useEffect(() => {
    setSosGroups(prev => prev.map(g => {
      let groupChanged = false;
      const officers = g.officers.map(o => {
        if (!o.selected) return o;
        if (checkDuplicate(officerMetadata, g.deployment, g.timingSegments, o.name).status !== 'duplicate') return o;
        groupChanged = true;
        return { ...o, selected: false };
      });
      return groupChanged ? { ...g, officers } : g;
    }));
  }, [officerMetadata]);

  // Duplicate status per officer, computed once per render and shared by both the summary
  // counts and the per-officer badge — checkDuplicate does an O(existing officers) scan,
  // so this avoids doing that scan twice per officer on every keystroke.
  const dupCheckByOfficerId = useMemo(() => {
    const map = new Map<number, DupCheck>();
    for (const g of sosGroups) {
      for (const o of g.officers) {
        map.set(o.id, checkDuplicate(officerMetadata, g.deployment, g.timingSegments, o.name));
      }
    }
    return map;
  }, [sosGroups, officerMetadata]);

  // Template rows clipped to the (possibly custom) availability window — drives both
  // the preview list and what actually gets submitted, so counters outside the window
  // the user picked are trimmed/dropped instead of silently kept.
  // Declared before the early return below so this Hook is called unconditionally
  // on every render (Rules of Hooks); only consumed in the generated branch.
  const trimmedMainRows = useMemo(
    () => trimRowsToAvailability(mainTemplateRows, mainAvailStart, mainAvailEnd, shiftStartHour),
    [mainTemplateRows, mainAvailStart, mainAvailEnd, shiftStartHour],
  );

  // The dialog is one step: each partially-seated officer arrives set to "keep", with the
  // window they can actually be seated for and the default breaks for THAT window (so a
  // window within the max ctr duration starts with none). The user flips an officer to
  // "skip" or changes their breaks on the same screen.
  useEffect(() => {
    if (partialOfficers.length === 0) return;
    const proposedMap = new Map<string, string>();
    const keepMap = new Map<string, boolean>();
    for (const po of partialOfficers) {
      const trimmed = computeAssignedTiming(po.timing, po.unassigned_times, shiftStartHour);
      // What a re-assign will really give them — shown, and sent back as the window to keep,
      // so the breaks the user sees are planned for the window they see.
      const proposed = trimmed ? (po.proposed_timing ?? trimmed) : '';
      proposedMap.set(po.officer_key, proposed);
      keepMap.set(po.officer_key, !!proposed);
    }
    setPartialProposed(proposedMap);
    setPartialKeep(keepMap);
    setPartialBreakSizes(new Map(partialOfficers.map(po => [po.officer_key, [null, null, null] as (number | null)[]])));
    for (const po of partialOfficers) {
      const proposed = proposedMap.get(po.officer_key);
      if (proposed) void handleResetPartialBreakToDefault(po, proposed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partialOfficers]);

  if (!generated) {
    return (
      <div className="ui-zoom" style={{ background: 'white', borderRadius: '8px', padding: '30px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h2>Generate a schedule first</h2>
        <p style={{ color: '#666' }}>You need to generate an initial roster before adding officers</p>
      </div>
    );
  }

  // ── SOS extraction ────────────────────────────────────────────────────────────

  const handleSOSExtract = (overrideText?: string) => {
    const inputText = overrideText ?? sosRawText;
    if (!inputText.trim()) { setSosExtractEmptyPressed(true); return; }
    setSosExtractEmptyPressed(false);
    setSosExtractFailed(false);

    // Groups sharing the same deployment + timing, keyed by "deployment||st1-et1,st2-et2,..."
    const groupMap = new Map<string, { deployment: string; timingSegments: Array<{ st: string; et: string }>; officers: Array<{ name: string }> }>();
    // Per-officer multi-window entries: keyed by "deployment||officerName"
    const multiWindowMap = new Map<string, { deployment: string; name: string; ranges: Array<{ st: string; et: string }> }>();

    let currentDeployment = '';
    let currentHeaderRanges: Array<{ st: string; et: string }> = [];
    const padTime = (t: string) => t.padStart(4, '0');

    // Compute shift-end HHMM for the "end" keyword (e.g. "Bus (0600-end)")
    const endOfShiftMins = shiftStartHour * 60 + 48 * 15;
    const endHHMM = `${String(Math.floor(endOfShiftMins / 60) % 24).padStart(2, '0')}${String(endOfShiftMins % 60).padStart(2, '0')}`;

    for (const rawLine of inputText.split('\n')) {
      // Strip zero-width & non-breaking spaces; normalise "hrs" suffix and "end" keyword
      const line = rawLine
        .replace(/[\u200B-\u200D\uFEFF\u2060\u00A0]/g, ' ')
        .replace(/(\d{3,4})\s*hrs\b/gi, '$1')
        .replace(/[-–]\s*end\b/gi, `-${endHHMM}`)
        .trim();
      if (!line) continue;

      const isOfficerLine = /^[-•]/.test(line);

      if (!isOfficerLine) {
        // Section header — extract deployment name and timing
        const headerText = line.replace(/^(?:✅|✓|☑|✔️)\s*/, '').trim();
        // Extract ALL time ranges from every (...) group in the header
        currentHeaderRanges = [];
        for (const pm of [...headerText.matchAll(/\(([^)]+)\)/g)]) {
          const rangeRe = /(\d{3,4})\s*[-–]\s*(\d{3,4})/g;
          let rm: RegExpExecArray | null;
          while ((rm = rangeRe.exec(pm[1])) !== null) {
            const st = padTime(rm[1]);
            const et = padTime(rm[2]);
            if (st && et) currentHeaderRanges.push({ st, et });
          }
        }
        // Fallback: time range written directly in header without parentheses
        if (currentHeaderRanges.length === 0) {
          const tm = headerText.match(/(\d{3,4})\s*[-–]\s*(\d{3,4})/);
          if (tm) {
            const st = padTime(tm[1]);
            const et = padTime(tm[2]);
            if (st && et) currentHeaderRanges = [{ st, et }];
          }
        }
        const dep = headerText
          .replace(/\s*:?\s*\(?\d{3,4}\s*[-–]\s*\d{3,4}\)?.*$/, '')
          .replace(/:$/, '')
          .replace(/^\d+\s*x?\s*/i, '').trim();
        if (dep) currentDeployment = dep;
      } else {
        // Officer line
        let officerText = line.replace(/^[-•]\s*/, '').trim();
        if (!officerText) continue;

        // Extract time ranges from ALL parenthesised groups so multi-window officers like
        // "Juliza (2200-2230) & (0900-1000)" capture both windows, not just the first.
        const inlineRanges: Array<{ st: string; et: string }> = [];
        for (const pm of [...officerText.matchAll(/\(([^)]+)\)/g)]) {
          const rangeRe = /(\d{3,4})\s*[-–]\s*(\d{3,4})/g;
          let m: RegExpExecArray | null;
          while ((m = rangeRe.exec(pm[1])) !== null) {
            const st = padTime(m[1]);
            const et = padTime(m[2]);
            if (st && et) inlineRanges.push({ st, et });
          }
        }
        // Strip everything from the first "(" onwards to get a clean officer name
        if (inlineRanges.length > 0) {
          officerText = officerText.replace(/\s*\(.*$/, '').trim();
        }
        if (!officerText) continue;

        if (!currentDeployment) continue;

        // Build the full set of time windows for this officer:
        // inline ranges come first, then any header ranges not already present.
        const officerRanges: Array<{ st: string; et: string }> = [...inlineRanges];
        for (const h of currentHeaderRanges) {
          if (!officerRanges.some(r => r.st === h.st && r.et === h.et)) {
            officerRanges.push(h);
          }
        }

        // Keep only ranges that fall within the current shift's valid time slots.
        // Out-of-range times are discarded so the user can fill them in manually.
        const validRanges = officerRanges.filter(r => TIME_SLOTS_15.includes(r.st) && TIME_SLOTS_15.includes(r.et));
        const validInlineRanges = inlineRanges.filter(r => TIME_SLOTS_15.includes(r.st) && TIME_SLOTS_15.includes(r.et));

        if (validInlineRanges.length > 0) {
          // Officer has own valid inline timing (possibly combined with header) → individual entry
          const multiKey = `${currentDeployment}||${officerText}`;
          if (!multiWindowMap.has(multiKey)) {
            multiWindowMap.set(multiKey, { deployment: currentDeployment, name: officerText, ranges: [] });
          }
          const existing = multiWindowMap.get(multiKey)!;
          for (const r of validRanges) {
            if (!existing.ranges.some(e => e.st === r.st && e.et === r.et)) {
              existing.ranges.push(r);
            }
          }
        } else {
          // Group by shared timing; if no valid timing found, leave blank for user to fill in
          const rangeKey = validRanges.map(r => `${r.st}-${r.et}`).join(',');
          const key = `${currentDeployment}||${rangeKey}`;
          if (!groupMap.has(key)) {
            const segs = validRanges.length > 0 ? [...validRanges] : [{ st: '', et: '' }];
            groupMap.set(key, { deployment: currentDeployment, timingSegments: segs, officers: [] });
          }
          groupMap.get(key)!.officers.push({ name: officerText });
        }
      }
    }

    const groups: SOSGroup[] = [
      // Groups sharing the same deployment + timing
      ...Array.from(groupMap.values()).map(g => ({
        id: nextId.current++,
        deployment: g.deployment,
        timingSegments: g.timingSegments,
        expanded: true,
        officers: g.officers.map(o => ({
          id: nextId.current++,
          name: o.name,
          // Auto-uncheck exact re-paste duplicates (same name + deployment + overlapping window)
          // so re-sending the running list defaults to only adding what's actually new — and, unless
          // turned off in Settings, anyone starting at the top of the shift, whom generation seated.
          selected: checkDuplicate(officerMetadata, g.deployment, g.timingSegments, o.name).status !== 'duplicate'
            && !(untickShiftStart && opensShift(g.timingSegments, TIME_SLOTS_15[0])),
        })),
        breakSizes: [null, null, null] as (number | null)[],
        breakDefaults: [null, null, null] as (number | null)[],
        breakModified: false,
      })),
      // Multi-window officers — one group per officer with individual segments
      ...Array.from(multiWindowMap.values()).map(mw => {
        const segs = mw.ranges.map(r => ({ st: r.st, et: r.et }));
        return {
          id: nextId.current++,
          deployment: mw.deployment,
          timingSegments: segs,
          expanded: true,
          officers: [{
            id: nextId.current++,
            name: mw.name,
            selected: checkDuplicate(officerMetadata, mw.deployment, segs, mw.name).status !== 'duplicate'
              && !(untickShiftStart && opensShift(segs, TIME_SLOTS_15[0])),
          }],
          breakSizes: [null, null, null] as (number | null)[],
          breakDefaults: [null, null, null] as (number | null)[],
          breakModified: false,
          };
      }),
    ];

    if (groups.length > 0) {
      setSosGroups(groups);
      void fetchSOSGroupDefaults(groups);
    } else {
      // Nothing parsed from non-empty input — surface the format the parser
      // expects instead of leaving the user staring at an unchanged, empty list.
      setSosExtractFailed(true);
      notifyWarning('No officers detected — please follow the format below.');
    }
  }

  // ── SOS break defaults (fetched from backend) ────────────────────────────────

  const fetchSOSGroupDefaults = async (groups: SOSGroup[]) => {
    const updates = await Promise.all(
      groups.map(async (g) => {
        const validSegs = g.timingSegments.filter(s => s.st && s.et);
        if (validSegs.length === 0) return null;
        const timing = validSegs.map(s => `${s.st}-${s.et}`).join(',');
        try {
          const { pattern } = await apiClient.getBreakPattern({
            timing, officer_type: 'sos', shift: shift ?? 'morning',
          });
          const sizes: (number | null)[] = [pattern[0] ?? null, pattern[1] ?? null, pattern[2] ?? null];
          return { id: g.id, sizes };
        } catch { return null; }
      })
    );
    setSosGroups(prev => prev.map(g => {
      const upd = updates.find(u => u?.id === g.id);
      if (!upd || g.breakModified) return g;
      return { ...g, breakSizes: upd.sizes, breakDefaults: upd.sizes };
    }));
  };

  // ── SOS helpers ───────────────────────────────────────────────────────────────

  const updateGroup = (id: number, patch: Partial<SOSGroup>) =>
    setSosGroups(prev => prev.map(g => g.id === id ? { ...g, ...patch } : g));

  const updateOfficer = (groupId: number, officerId: number, patch: Partial<SOSOfficerItem>) =>
    setSosGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, officers: g.officers.map(o => o.id === officerId ? { ...o, ...patch } : o) }
        : g
    ));

  const removeOfficer = (groupId: number, officerId: number) =>
    setSosGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, officers: g.officers.filter(o => o.id !== officerId) }
        : g
    ));

  const addOfficerToGroup = (groupId: number) =>
    setSosGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, officers: [...g.officers, { id: nextId.current++, name: '', selected: true }] }
        : g
    ));

  const addSegmentToGroup = (groupId: number) =>
    setSosGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, timingSegments: [...g.timingSegments, { st: '', et: '' }] }
        : g
    ));

  const removeSegmentFromGroup = (groupId: number, segIdx: number) => {
    setSosGroups(prev => {
      const updated = prev.map(g =>
        g.id === groupId
          ? { ...g, timingSegments: g.timingSegments.filter((_, i) => i !== segIdx) }
          : g
      );
      const changedGroup = updated.find(g => g.id === groupId);
      if (changedGroup && !changedGroup.breakModified) {
        void fetchSOSGroupDefaults([changedGroup]);
      }
      return updated;
    });
  };

  const updateGroupBreakSize = (groupId: number, breakIdx: number, val: number | null) =>
    setSosGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, breakSizes: g.breakSizes.map((s, i) => i === breakIdx ? val : s), breakModified: true }
        : g
    ));

  const resetGroupBreaks = async (groupId: number) => {
    const group = sosGroups.find(g => g.id === groupId);
    if (!group) return;
    const validSegs = group.timingSegments.filter(s => s.st && s.et);
    if (validSegs.length === 0) return;
    const timing = validSegs.map(s => `${s.st}-${s.et}`).join(',');
    try {
      const { pattern } = await apiClient.getBreakPattern({
        timing, officer_type: 'sos', shift: shift ?? 'morning',
      });
      const sizes: (number | null)[] = [pattern[0] ?? null, pattern[1] ?? null, pattern[2] ?? null];
      setSosGroups(prev => prev.map(g =>
        g.id === groupId ? { ...g, breakSizes: sizes, breakDefaults: sizes, breakModified: false } : g
      ));
    } catch { /* keep current */ }
  };

  const updateSegment = (groupId: number, segIdx: number, patch: { st?: string; et?: string }) => {
    setSosGroups(prev => {
      const updated = prev.map(g =>
        g.id === groupId
          ? { ...g, timingSegments: g.timingSegments.map((s, i) => i === segIdx ? { ...s, ...patch } : s) }
          : g
      );
      const changedGroup = updated.find(g => g.id === groupId);
      if (changedGroup && !changedGroup.breakModified) {
        void fetchSOSGroupDefaults([changedGroup]);
      }
      return updated;
    });
  };

  const addEmptyGroup = () =>
    setSosGroups(prev => [...prev, {
      id: nextId.current++,
      deployment: '',
      timingSegments: [{ st: '', et: '' }],
      expanded: true,
      officers: [{ id: nextId.current++, name: '', selected: true }],
      breakSizes: [null, null, null] as (number | null)[],
      breakDefaults: [null, null, null] as (number | null)[],
      breakModified: false,
    }]);

  // ── SOS confirm ───────────────────────────────────────────────────────────────

  const handleSOSConfirm = async () => {
    // Build one record per officer in the pasted batch — selected or not — so the Inbox
    // can report every auto-detected duplicate/extend regardless of whether the user left
    // it checked or unchecked. Only selected ones become an API entry.
    // The backend's own name-deduplication (e.g. "Isbandi" -> "Isbandi2") means matching
    // its response back to this by NAME would silently fail exactly for flagged rows —
    // a name collision is what flags them in the first place. Position is reliable
    // instead: this function only ever submits 'sos' entries, and apply_officer_entries's
    // sos_entries filter + rename preserve list order 1:1 relative to what was submitted.
    let selectedIdx = -1;
    const officerBuilds = sosGroups.flatMap(g => {
      const validSegs = g.timingSegments.filter(s => s.st && s.et);
      if (validSegs.length === 0) return [];
      const timing = validSegs.map(s => `${s.st}-${s.et}`).join(',');
      // Only send custom_break_sizes when the user explicitly changed the defaults.
      const effectiveSizes = g.breakSizes.filter((s): s is number => s !== null);
      const customBreakSizes = sosAutoAssign && g.breakModified && effectiveSizes.length > 0 ? effectiveSizes : undefined;
      return g.officers.map(o => {
        if (o.selected) selectedIdx++;
        const check = dupCheckByOfficerId.get(o.id);
        return {
          name: o.name,
          deployment: g.deployment,
          timing,
          selected: o.selected,
          selectedIndex: o.selected ? selectedIdx : -1,
          entry: o.selected ? {
            officer_type: 'sos' as const,
            name: o.name || undefined,
            deployment: g.deployment || undefined,
            timing,
            break_duration: undefined,
            auto_assign: sosAutoAssign,
            custom_break_sizes: customBreakSizes,
          } : null,
          dupStatus: check?.status,
          existingWindowsText: check ? formatDuplicateNote(check.existingWindows, g.deployment, o.name) : '',
        };
      });
    });
    const entries = officerBuilds.filter(b => b.entry).map(b => b.entry!);

    if (entries.length === 0) {
      notifyWarning('No officers selected, or all selected groups have incomplete timing');
      return;
    }
    if (entries.length > 50) {
      notifyError('Max 50 SOS officers at a time. Uncheck some and add the rest separately.');
      return;
    }

    // No "Adding…" toast here — the Add button already shows its own spinner
    // state, and stacking a toast on top of that was just a second copy of the
    // same signal.
    setSOSLoading(true);

    try {
      const response = await apiClient.addOfficers({ officer_entries: entries });

      // Show confirmation dialog for partial or fully-failed officers
      const partials = response.partially_assigned ?? [];
      const failed = (response.skipped_officers ?? []).filter(o => o.officer_type === 'sos');
      // Referenced by the Inbox log below — processed_entries lines up 1:1 with
      // selectedIndex (see officerBuilds above).
      const sourceEntries = response.processed_entries?.filter(e => e.officer_type === 'sos') ?? [];
      const pendingKeys = new Set([...partials, ...failed].map(o => o.officer_key));

      if (onOperationMessage) {
        // Every batch gets logged to the Inbox as a complete record — one row per
        // officer submitted, selected or not. Clean rows just show a blank Remarks;
        // only flagged/failed rows carry a warning.
        const rows = officerBuilds.map(b => {
          const isFlagged = b.dupStatus === 'duplicate' || b.dupStatus === 'extend';
          const existingWindows = isFlagged ? b.existingWindowsText : undefined;
          if (!b.selected) {
            return { name: b.name, deployment: b.deployment, timing: b.timing, success: true, skipped: true, existingWindows };
          }
          const resp = sourceEntries[b.selectedIndex];
          if (resp && !resp.success) {
            return { officerKey: resp.officer_key, name: resp.name ?? b.name, deployment: b.deployment, timing: b.timing, success: false, reason: resp.failure_reason, existingWindows };
          }
          return { officerKey: resp?.officer_key, name: resp?.name ?? b.name, deployment: b.deployment, timing: b.timing, success: true, existingWindows };
        });

        if (partials.length > 0 || failed.length > 0) {
          // A partial/failed dialog is about to show — don't log this batch as a
          // finished action yet. Stash the rows for officers unaffected by that dialog
          // so the eventual keep/remove decision can emit ONE message for the whole
          // batch instead of splitting it into two. Matched by officer_key (reliable,
          // unique) rather than name/deployment/timing (fragile — those can collide).
          setPendingCleanRows(rows.filter(r => !r.officerKey || !pendingKeys.has(r.officerKey)));
        } else {
          onOperationMessage({
            type: 'sos_add',
            timestamp: new Date().toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore' }),
            snapshotTimestamp: response.snapshot?.timestamp,
            rows,
          });
        }
      }
      if (partials.length > 0 || failed.length > 0) {
        dismissSpinner();
        setPartialOfficers(partials);
        setFailedOfficers(failed);
        setSOSLoading(false);
        return; // Wait for user to dismiss / decide on partial assignments
      }

      setSosGroups([]);
      setSOSRawText('');
      notifySuccess(response.message);
      onAdded?.(response);
      onSwitchTab?.('counter');
    } catch (err) {
      notifyError(`Failed to add SOS officers: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSOSLoading(false);
    }
  };

  // One partial officer's default break sizes for the window they will hold — set when the
  // dialog opens, and again whenever the user presses "Reset to default".
  const handleResetPartialBreakToDefault = async (po: PartiallyAssignedOfficer, trimmedTiming: string) => {
    const isOT = po.officer_type === 'ot' || po.officer_key?.startsWith('OT');
    try {
      const { pattern } = await apiClient.getBreakPattern({
        timing: trimmedTiming,
        officer_type: isOT ? 'ot' : 'sos',
        shift: shift ?? 'morning',
      });
      const sizes: (number | null)[] = [pattern[0] ?? null, pattern[1] ?? null, pattern[2] ?? null];
      setPartialBreakSizes(prev => {
        const next = new Map(prev);
        next.set(po.officer_key, sizes);
        return next;
      });
    } catch {
      notifyError(`Failed to compute default break for ${po.name || po.officer_key}`);
    }
  };

  /** Which kind of officer the open counter-assignment dialog is about. */
  const isOTDialog = () =>
    (failedOfficers.length > 0 && failedOfficers[0].officer_type === 'ot') ||
    (partialOfficers.length > 0 && (partialOfficers[0].officer_type === 'ot' || partialOfficers[0].officer_key?.startsWith('OT')));

  /**
   * ✕ on the counter-assignment dialog: take the WHOLE add back, not just the
   * officers the dialog is asking about.
   *
   * Keep/Remove Partial both leave the cleanly-assigned officers of the same
   * batch on the roster, so neither is a way out for someone who reads "counters
   * are full" as "this isn't the add I meant to make". The batch went in as one
   * history version, so one undo removes all of it — the same state as never
   * having pressed Add. The pasted list is deliberately left on screen: cancel
   * means "not like this", and retyping it would be the punishment for saying so.
   */
  const handleCancelAdd = async () => {
    if (cancellingAdd) return;
    setCancellingAdd(true);
    const wasOT = isOTDialog();
    let restored: RosterState;
    try {
      restored = await apiClient.undoRoster();
    } catch (err) {
      notifyError(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setCancellingAdd(false);
      return;
    }

    // One Inbox record for the cancelled action: every officer of the batch, none
    // of them added. Without it the batch would leave no trace at all, and "did
    // that add go through?" is exactly what the Inbox exists to answer.
    if (onOperationMessage) {
      const rows: SOSOperationRow[] = [
        ...pendingCleanRows,
        ...partialOfficers.map(po => ({
          officerKey: po.officer_key, name: po.name, deployment: po.deployment,
          timing: po.timing, success: true, partialTiming: po.unassigned_times,
        })),
        ...failedOfficers.map(fo => ({
          officerKey: fo.officer_key, name: fo.name, deployment: fo.deployment,
          timing: fo.timing, success: true, reason: fo.reason,
        })),
      ].map(row => ({ ...row, skipped: true }));
      if (rows.length > 0) {
        onOperationMessage({
          type: wasOT ? 'ot_add' : 'sos_add',
          timestamp: new Date().toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore' }),
          rows,
        });
      }
    }

    setPendingCleanRows([]);
    setPartialOfficers([]);
    setFailedOfficers([]);
    setCancellingAdd(false);
    dismissSpinner();
    notifyInfo(`Add cancelled — no ${wasOT ? 'OT' : 'SOS'} officers were added`);
    if (onStateRestored) onStateRestored(restored);
    else onAdded?.();
  };

  const confirmPartialDecision = async () => {
    if (partialDeciding) return;
    setPartialDeciding(true);
    try {
      await handlePartialDecision();
    } finally {
      setPartialDeciding(false);
    }
  };

  const handlePartialDecision = async () => {
    const isOT = isOTDialog();
    const typeLabel = isOT ? 'OT' : 'SOS';

    // Rows for the partial officers, populated by whichever branch below actually runs —
    // combined with pendingCleanRows (the rest of the same batch) and the fully-failed
    // officers into ONE Inbox message for the whole action, emitted once at the end.
    let partialOutcomeRows: SOSOperationRow[] = [];
    // Last mutation's own snapshot from whichever branch below runs, so the caller can
    // patch the grid instantly instead of waiting on a refetch round trip.
    let mutationResult: Pick<OperationResult, 'snapshot'> | undefined;

    const keepOf = (po: PartiallyAssignedOfficer) =>
      partialKeep.get(po.officer_key) !== false && !!partialProposed.get(po.officer_key);
    const skipped = partialOfficers.filter(po => !keepOf(po));
    const kept = partialOfficers.filter(keepOf);

    if (skipped.length > 0) {
      // Skipped officers come off the roster entirely
      try {
        // One request for the lot: one roster version and one broadcast, not one per officer.
        mutationResult = await foldIntoPrevious(() => apiClient.removeOfficers(skipped.map(po => po.officer_key)));
        partialOutcomeRows = skipped.map(po => ({
          officerKey: po.officer_key, name: po.name, deployment: po.deployment, timing: po.timing,
          success: true, partialTiming: po.unassigned_times, removed: true,
        }));
      } catch (err) {
        notifyError('Failed to remove skipped officers');
      }
    }
    if (kept.length > 0) {
      // Batch re-assign with trimmed availability (single history entry for all officers)
      notifyInfo(`Adjusting availability and re-assigning ${kept.length} ${typeLabel} officer(s)…`, true);
      const adjustedRows: SOSOperationRow[] = [];
      const batchEntries: Array<{
        officer_key: string;
        original_start: string;
        original_end: string;
        avail_start: string;
        avail_end: string;
        segments?: Array<{ start: string; end: string }>;
        custom_break_sizes?: number[];
      }> = [];

      for (const po of kept) {
        const trimmedTiming = partialProposed.get(po.officer_key) ?? '';
        if (!trimmedTiming) continue;

        // Earliest start to latest end on the shift's clock — never the first
        // and last segment as written. An ops-room line lists windows in any
        // order, and on a night shift "0900-1000,2200-2300" begins at 2200:
        // read positionally it spans 0900 to 2300, a range the backend refuses
        // outright ("Start time must be before end time").
        const original = timingSpan(po.timing, shiftStartHour);
        const trimmed = timingSpan(trimmedTiming, shiftStartHour);
        if (!original || !trimmed) continue;

        // computeAssignedTiming returns its windows in chronological order, so
        // this list is already the order the roster reads them in.
        const trimmedSegs = trimmedTiming.split(',').map(s => s.trim()).filter(Boolean);

        // Every break set to "none" is a decision to plan no breaks — send the empty list,
        // not nothing: an absent list means "use the default pattern", which would put the
        // breaks straight back and open the very gaps the user just removed.
        const customSizes = (partialBreakSizes.get(po.officer_key) ?? []).filter((s): s is number => s !== null);

        batchEntries.push({
          officer_key: po.officer_key,
          original_start: original.start,
          original_end: original.end,
          avail_start: trimmed.start,
          avail_end: trimmed.end,
          segments: trimmedSegs.length > 1
            ? trimmedSegs.map(s => { const [st, et] = s.split('-'); return { start: st, end: et }; })
            : undefined,
          custom_break_sizes: customSizes,
        });

        adjustedRows.push({
          officerKey: po.officer_key,
          name: po.name,
          deployment: po.deployment,
          timing: po.timing,
          success: true,
          partialTiming: po.unassigned_times,
          adjustedTiming: trimmedTiming,
        });
      }

      try {
        const again = await foldIntoPrevious(() => apiClient.reassignPartialOfficers({ officers: batchEntries }));
        mutationResult = again;
        // Seating an officer can take a further stretch off their window; the Inbox row says
        // the window they finally hold, not the one that was asked for.
        partialOutcomeRows = [...partialOutcomeRows, ...adjustedRows.map(row => ({
          ...row,
          adjustedTiming: (row.officerKey && again.adjusted_timings?.[row.officerKey]) || row.adjustedTiming,
        }))];
        dismissSpinner();
        // The breaks were planned again for the shorter window, which can leave a stretch with no
        // counter after all — or none at all. Those with nothing come off the roster; those with a
        // gap are asked about again, against the window they now have, instead of left with a hole.
        const lost = again.skipped_officers ?? [];
        const lostKeys = lost.flatMap(fo => (fo.officer_key ? [fo.officer_key] : []));
        if (lostKeys.length > 0) mutationResult = await foldIntoPrevious(() => apiClient.removeOfficers(lostKeys));
        if (lost.length > 0) {
          notifyWarning(
            `Removed — no counter free for more than 30 min once breaks were re-planned: ` +
            lost.map(fo => fo.name || fo.officer_key).join(', '),
          );
        }
        const holes = again.partially_assigned ?? [];
        if (holes.length > 0) {
          setPartialOfficers(holes);
          setFailedOfficers([]);
          notifyWarning('Re-planning breaks for the shorter window left a gap — please check these again.');
          onAdded?.(mutationResult);
          return;
        }
        // No success toast here — the partial-availability dialog the user just acted on
        // already surfaced this outcome; the one notifySuccess below (after failedOfficers
        // is also accounted for) is the single notification for the whole decision.
      } catch (err) {
        dismissSpinner();
        notifyError(`Failed to re-assign partial officers: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    if (onOperationMessage) {
      const failedRows: SOSOperationRow[] = failedOfficers.map(fo => ({
        officerKey: fo.officer_key, name: fo.name, deployment: fo.deployment, timing: fo.timing,
        success: false, reason: fo.reason,
      }));
      const rows = [...pendingCleanRows, ...partialOutcomeRows, ...failedRows];
      if (rows.length > 0) {
        onOperationMessage({
          type: isOT ? 'ot_add' : 'sos_add',
          timestamp: new Date().toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore' }),
          rows,
        });
      }
    }
    setPendingCleanRows([]);

    const hasFailed = failedOfficers.length > 0;
    setPartialOfficers([]);
    setFailedOfficers([]);
    if (!isOT) {
      setSosGroups([]);
      setSOSRawText('');
    }
    // Exactly one short success toast for the whole decision — the partial-availability
    // dialog the user just acted on already showed the per-officer outcome, so this is
    // just a plain headline, not a re-statement of it.
    const caveats = [
      skipped.length > 0 ? `${skipped.length} skipped` : null,
      hasFailed ? 'some failed' : null,
    ].filter(Boolean).join(', ');
    const headline = kept.length > 0 && !hasFailed ? `${typeLabel} officers re-assigned` : `${typeLabel} officers added`;
    notifySuccess(caveats ? `${headline} (${caveats})` : headline);
    onAdded?.(mutationResult);
    onSwitchTab?.('counter');
  };

  // ── OT handlers ───────────────────────────────────────────────────────────────

  const handleOTConfirm = async () => {
    if (!otTimeValid) { notifyWarning('Set a valid time range first'); return; }
    const effectiveSizes = otBreakSizes.filter((s): s is number => s !== null);
    const entries = Array.from({ length: otNumOfficers }).map(() => ({
      officer_type: 'ot' as const,
      timing: `${otStartTime}-${otEndTime}`,
      auto_assign: otAutoAssign,
      custom_break_sizes: otAutoAssign && otBreakModified && effectiveSizes.length > 0 ? effectiveSizes : undefined,
      // Only meaningful at HOTO; the backend ignores it on any other window.
      // Pinned on each officer (and saved with the session), so every later re-plan of
      // theirs counts breaks to the same time.
      stays_until: otBreakStarts?.hoto ? otBreakStarts.stays_until : undefined,
      break_starts: otAutoAssign ? breakStartsPayload(otBreakStarts?.options, otBreakStartsChecked) : undefined,
      allow_3h_last_break: otAutoAssign && otAllow3hLastBreak ? true : undefined,
    }));

    // No "Adding…" toast — the Add button's own spinner already covers it.
    setOTLoading(true);

    try {
      const response = await apiClient.addOfficers({ officer_entries: entries });

      const otSkipped = response.skipped_officers?.filter(s => s.officer_type === 'ot') ?? [];
      const otPartials = response.partially_assigned?.filter(
        p => p.officer_type === 'ot' || p.officer_key?.startsWith('OT')
      ) ?? [];

      if (onOperationMessage) {
        // OT officers are anonymous (no name), so rows are built from processed_entries
        // purely to pick up each one's assigned officer key.
        const sourceEntries = response.processed_entries?.filter(e => e.officer_type === 'ot') ?? [];
        const rows = sourceEntries.map(e => ({ officerKey: e.officer_key, timing: e.timing, success: true }));

        if (otSkipped.length > 0 || otPartials.length > 0) {
          // A partial/failed dialog is about to show — defer logging this batch until
          // the keep/remove decision, same as the SOS flow, so the whole action (however
          // it's resolved) ends up as ONE Inbox message instead of two. Matched by
          // officer_key rather than position — OT officers are interchangeable, so which
          // specific key ends up excluded doesn't matter, only that the counts are right.
          const pendingKeys = new Set([...otPartials, ...otSkipped].map(o => o.officer_key));
          setPendingCleanRows(rows.filter(r => !r.officerKey || !pendingKeys.has(r.officerKey)));
        } else {
          onOperationMessage({
            type: 'ot_add',
            timestamp: new Date().toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore' }),
            snapshotTimestamp: response.snapshot?.timestamp,
            rows,
          });
        }
      }

      if (otSkipped.length > 0 || otPartials.length > 0) {
        dismissSpinner();
        setFailedOfficers(otSkipped);
        setPartialOfficers(otPartials);
        setOTLoading(false);
        return;
      }

      notifySuccess(response.message);
      onAdded?.(response);
      onSwitchTab?.('counter');
    } catch (err) {
      notifyError(`Failed to add OT officers: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setOTLoading(false);
    }
  };

  // ── Main officer confirm ──────────────────────────────────────────────────────

  // Maximum SN depends on mode (arrival=40, departure=36) – fall back to 40
  const MAX_SN = mode === 'departure' ? 36 : 40;
  const unusedSns = Array.from({ length: MAX_SN }, (_, i) => i + 1).filter(n => !usedSns.includes(n));

  const handleMainConfirm = async () => {
    if (!mainSN) { notifyWarning('Select a serial number'); return; }
    // No "Adding…" toast — the Add button's own loading state already covers it.
    setMainLoading(true);
    try {
      const assignments = trimmedMainRows.map(r => ({
        officer: `SN${mainSN}`, type: 'MAIN' as const, counter: r.counter,
        start: r.start, end: r.end, duration: minutesSpan(r.start, r.end),
      }));
      const response = await apiClient.addMainOfficer({
        sn_number: mainSN,
        name: mainName || undefined,
        assignments,
        availability_start: mainAvailStart,
        availability_end: mainAvailEnd,
      });
      notifySuccess(response.message);
      setMainSN(0);
      setMainName('');
      setMainTemplateRows([]);
      setMainAvailStart(TIME_SLOTS_15[0]);
      setMainAvailEnd(TIME_SLOTS_15[TIME_SLOTS_15.length - 1]);
      onAdded?.(response);
      apiClient.getUsedSns().then(r => setUsedSns(r.used_sns)).catch(() => {});
    } catch (err) {
      notifyError(`Failed to add officer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setMainLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────────

  const totalSOSSelected = sosGroups.reduce((a, g) => a + g.officers.filter(o => o.selected).length, 0);
  const totalOTCount = otNumOfficers;
  const otTimeValid = isValidTimeRange(otStartTime, otEndTime, shift ?? 'morning');
  const hasInvalidTiming = sosGroups.some(g => g.timingSegments.every(s => !s.st || !s.et));

  // Duplicate-status breakdown across all extracted officers (paste review summary)
  const sosDupCounts = sosGroups.reduce((acc, g) => {
    for (const o of g.officers) {
      acc[dupCheckByOfficerId.get(o.id)?.status ?? 'new']++;
    }
    return acc;
  }, { new: 0, duplicate: 0, extend: 0, elsewhere: 0 } as Record<DupStatus, number>);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="add-officers-card" style={{ background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      {/* No heading here — the drawer's sticky header names this panel. */}

      {/* Desktop sub-tabs (hidden on mobile — drawer header takes over) */}
      <div className="desktop-subtabs">
        {(['main', 'sos', 'ot'] as const).map(tab => {
          const active = subTab === tab;
          return (
            <button
              key={tab}
              onClick={() => onSubTabChange(tab)}
              style={{
                padding: '5px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                background: active ? TAB_ACTIVE[tab].background : 'transparent',
                color: active ? TAB_ACTIVE[tab].color : '#666',
                border: 'none',
                borderBottom: active ? `2px solid ${TAB_ACTIVE[tab].borderColor}` : '2px solid transparent',
              }}
            >
              {tab === 'main' ? 'Main Officer' : tab === 'sos' ? 'SOS Officers' : 'OT Officers'}
            </button>
          );
        })}
      </div>

      {/* ── SOS TAB ─────────────────────────────────────────────────────────── */}
      {subTab === 'sos' && (
        <div>

          {/* Step 1: Assignment mode selector */}
          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', marginBottom: 14 }}>
            <button
              onClick={() => setSosAutoAssign(true)}
              style={{
                flex: 1, padding: '9px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                background: sosAutoAssign ? '#ffe4ee' : '#f8fafc',
                color: sosAutoAssign ? '#9d174d' : '#64748b',
                fontWeight: sosAutoAssign ? 700 : 400,
                borderRight: '1px solid #e2e8f0',
              }}
            >
              🤖 Auto-assign counters
            </button>
            <button
              onClick={() => setSosAutoAssign(false)}
              style={{
                flex: 1, padding: '9px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                background: !sosAutoAssign ? '#f0fdf4' : '#f8fafc',
                color: !sosAutoAssign ? '#166534' : '#64748b',
                fontWeight: !sosAutoAssign ? 700 : 400,
              }}
            >
              ✋ I'll assign manually
            </button>
          </div>
          {!sosAutoAssign && (
            <p style={{ fontSize: 12, color: '#166534', marginBottom: 12, marginTop: -8, padding: '5px 8px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
              Officers will be added with availability markers only. Drag on their row in the schedule grid to assign counters.
            </p>
          )}

          {/* Step 2: Paste / extract */}
          {/* Example format — hidden by default (it took space on every load for a
              reference most pastes never need); shown only when Extract runs on
              non-empty text and finds no officers, right when it's actually useful. */}
          {sosExtractFailed && (
            <div style={{ marginBottom: '6px', padding: '6px 8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
              <p style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, margin: '0 0 6px' }}>
                ⚠ No officers detected — please follow the format below.
              </p>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(exampleText).then(() => {
                      setExampleCopied(true);
                      setTimeout(() => setExampleCopied(false), 2000);
                    });
                  }}
                  style={{
                    position: 'absolute', top: 5, right: 8, background: 'transparent',
                    border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer',
                    padding: '4px 6px', fontSize: 14, lineHeight: 1,
                    color: exampleCopied ? '#22c55e' : '#666',
                  }}
                >
                  {exampleCopied ? '✓' : '⎘'}
                </button>
                <pre style={{ background: '#f8f9fa', padding: '5px 36px 5px 8px', borderRadius: 6, fontSize: 12, overflow: 'auto', margin: 0 }}>
                  {exampleText}
                </pre>
              </div>
            </div>
          )}

          {/* Paste area + Extract controls — sticky so they stay visible when scrolling through groups.
              Only once groups exist: before that there is nothing to scroll through, and
              a pinned block taller than the minimized sheet covered Extract / Add SOS
              manually and stopped the body from scrolling to them. */}
          <div style={{
            position: sosGroups.length > 0 ? 'sticky' : 'static', top: 0, background: 'white', zIndex: 5,
            paddingBottom: 8,
            borderBottom: sosGroups.length > 0 ? '1px solid #e2e8f0' : 'none',
            marginBottom: sosGroups.length > 0 ? 8 : 0,
          }}>
            <textarea
              placeholder="Paste the ops room message here — groups are extracted automatically on paste"
              value={sosRawText}
              onChange={(e) => { setSOSRawText(e.target.value); if (e.target.value.trim()) setSosExtractEmptyPressed(false); }}
              onPaste={(e) => {
                const el = e.currentTarget;
                const pasted = e.clipboardData?.getData('text');
                if (pasted) {
                  // Desktop / browsers that expose clipboard data synchronously
                  e.preventDefault();
                  const newText = el.value.slice(0, el.selectionStart) + pasted + el.value.slice(el.selectionEnd);
                  setSOSRawText(newText);
                  setTimeout(() => handleSOSExtract(newText), 0);
                } else {
                  // Mobile fallback: let the browser paste naturally, then read el.value
                  setTimeout(() => {
                    const v = el.value;
                    if (v) { setSOSRawText(v); handleSOSExtract(v); }
                  }, 150);
                }
              }}
              rows={sosGroups.length > 0 ? 3 : 6}
              style={{
                width: '100%', padding: '8px', borderRadius: 6,
                border: sosExtractEmptyPressed ? '1px solid #dc2626' : '1px solid #ddd',
                fontSize: 13, fontFamily: 'monospace', resize: 'vertical', marginBottom: 8,
                boxSizing: 'border-box',
              }}
            />
            {sosExtractEmptyPressed && (
              <p style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, margin: '-4px 0 8px' }}>
                Nothing to extract — paste an ops room message above first.
              </p>
            )}

            {sosGroups.length === 0 ? (
              // The initial choice: extract from a paste (the default, guided
              // path — styled as the prominent action) or build a deployment
              // by hand. Placed on one row with "OR" between them rather than
              // as a small Extract button plus a big dashed block further
              // down, which made manual entry look like the primary option
              // when paste-extract is what fits nearly every real paste.
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={() => handleSOSExtract()}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 6, border: 'none',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    background: sosRawText.trim() ? '#6366f1' : '#e2e8f0',
                    color: sosRawText.trim() ? 'white' : '#94a3b8',
                  }}
                >
                  ⚡ Extract
                </button>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>OR</span>
                <button
                  onClick={addEmptyGroup}
                  style={{
                    flex: 1, padding: '8px 12px', background: '#f1f5f9',
                    border: '1px solid #cbd5e1', borderRadius: 6,
                    fontSize: 12, cursor: 'pointer', color: '#475569', fontWeight: 600,
                  }}
                >
                  + Add SOS manually
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleSOSExtract()}
                  style={{
                    padding: '5px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                    border: sosRawText.trim() ? '1px solid #6366f1' : '1px solid #e2e8f0',
                    background: sosRawText.trim() ? '#eef2ff' : '#f1f5f9',
                    color: sosRawText.trim() ? '#4338ca' : '#94a3b8',
                  }}
                >
                  ⚡ Extract
                </button>
                <button
                  onClick={() => { setSosGroups([]); setSOSRawText(''); setSosExtractFailed(false); setSosExtractEmptyPressed(false); }}
                  style={{ padding: '5px 10px', background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12, cursor: 'pointer', color: '#94a3b8' }}
                >
                  🗑 Clear
                </button>
                {/* Kept short so it fits on the Extract/Clear row without wrapping — the
                    "different hours" / "same name elsewhere" detail lives on each group's
                    own row below (✓ / ↔ / ⚠ markers), so it doesn't need repeating here. */}
                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>{sosDupCounts.new} new</span>
                  {sosDupCounts.duplicate > 0 && (
                    <span style={{ color: '#94a3b8' }}> · {sosDupCounts.duplicate} alr added</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Group cards */}
          {sosGroups.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {sosGroups.map(group => {
                const selectedCount = group.officers.filter(o => o.selected).length;
                const allSelected = selectedCount === group.officers.length;
                const someSelected = selectedCount > 0 && !allSelected;
                const timingOk = group.timingSegments.some(s => s.st && s.et);
                return (
                  <div
                    key={group.id}
                    style={{
                      border: `1px solid ${timingOk ? '#e2e8f0' : '#fca5a5'}`,
                      borderRadius: 8, overflow: 'hidden', background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                  >
                    {/* Row 1: checkbox + deployment input + count + expand + remove */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px 4px',
                      background: timingOk ? '#f8fafc' : '#fff5f5',
                    }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => { if (el) el.indeterminate = someSelected; }}
                        onChange={e => updateGroup(group.id, {
                          officers: group.officers.map(o => ({ ...o, selected: e.target.checked }))
                        })}
                        style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                      />
                      <input
                        type="text"
                        value={group.deployment}
                        onChange={e => updateGroup(group.id, { deployment: e.target.value })}
                        placeholder="Deployment"
                        style={{
                          flex: 1, minWidth: 0, padding: '2px 6px', border: '1px solid #e2e8f0',
                          borderRadius: 4, fontSize: 12, fontWeight: 600, background: 'white',
                        }}
                      />
                      <span style={{
                        background: selectedCount > 0 ? '#dcfce7' : '#f1f5f9',
                        color: selectedCount > 0 ? '#166534' : '#94a3b8',
                        borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 600, flexShrink: 0,
                      }}>
                        {selectedCount}/{group.officers.length}
                      </span>
                      <button
                        onClick={() => updateGroup(group.id, { expanded: !group.expanded })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, padding: '0 2px', flexShrink: 0 }}
                      >
                        {group.expanded ? '▲' : '▼'}
                      </button>
                      <button
                        onClick={() => setSosGroups(prev => prev.filter(g => g.id !== group.id))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 18, padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
                        title="Remove group"
                      >
                        ×
                      </button>
                    </div>

                    {/* Row 2: timing segments + group break plan */}
                    <div style={{
                      padding: '4px 12px 8px 34px',
                      background: timingOk ? '#f8fafc' : '#fff5f5',
                      borderBottom: group.expanded ? '1px solid #e2e8f0' : 'none',
                    }}>
                      {untickShiftStart && opensShift(group.timingSegments, TIME_SLOTS_15[0]) && (
                        <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4 }}>
                          Starts at {fmtTime(TIME_SLOTS_15[0])}, so normally seated when the roster was generated — unticked.
                          Tick to add anyway; change this default in Settings.
                        </div>
                      )}
                      {group.timingSegments.map((seg, segIdx) => (
                        <div key={segIdx} style={{ marginBottom: segIdx < group.timingSegments.length - 1 ? 8 : 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <select
                              value={seg.st}
                              onChange={e => updateSegment(group.id, segIdx, { st: e.target.value })}
                              style={{
                                padding: '3px 4px', border: `1px solid ${seg.st ? '#d1d5db' : '#fca5a5'}`,
                                borderRadius: 4, fontSize: 12, background: 'white', minWidth: 80,
                              }}
                            >
                              {!seg.st && <option value="">-- start --</option>}
                              {TIME_SLOTS_15.map(t => <option key={t} value={t}>{fmtTime(t)}</option>)}
                            </select>
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>–</span>
                            <select
                              value={seg.et}
                              onChange={e => updateSegment(group.id, segIdx, { et: e.target.value })}
                              style={{
                                padding: '3px 4px', border: `1px solid ${seg.et ? '#d1d5db' : '#fca5a5'}`,
                                borderRadius: 4, fontSize: 12, background: 'white', minWidth: 80,
                              }}
                            >
                              {!seg.et && <option value="">-- end --</option>}
                              {TIME_SLOTS_15.map(t => <option key={t} value={t}>{fmtTime(t)}</option>)}
                            </select>
                            {/* Duration pill */}
                            {(() => {
                              if (!seg.st || !seg.et) return null;
                              try {
                                const startHourLocal = getStartHour(shift ?? 'morning');
                                const n = Math.max(0, hmmmToEndSlot(seg.et, startHourLocal) - hmmmToSlot(seg.st, startHourLocal));
                                if (n <= 0) return null;
                                const mins = n * 15;
                                const p = availPill(n);
                                const label = mins >= 60
                                  ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`
                                  : `${mins}m`;
                                return (
                                  <span style={{
                                    padding: '1px 7px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                                    background: p.bg, color: p.color,
                                    border: `3px solid ${p.border}`, flexShrink: 0,
                                  }}>{label}</span>
                                );
                              } catch { return null; }
                            })()}
                            {group.timingSegments.length > 1 && (
                              <button
                                onClick={() => removeSegmentFromGroup(group.id, segIdx)}
                                title="Remove this time window"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 15, lineHeight: 1, padding: '0 2px' }}
                              >×</button>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* Group-level break plan (auto-assign mode only) */}
                      {sosAutoAssign && (
                        <div style={{ marginTop: 6 }}>
                          <BreakSizeSelects
                            values={group.breakSizes}
                            onChange={(bIdx, val) => updateGroupBreakSize(group.id, bIdx, val)}
                            onReset={() => void resetGroupBreaks(group.id)}
                          />
                        </div>
                      )}
                      <button
                        onClick={() => addSegmentToGroup(group.id)}
                        style={{
                          marginTop: 4, padding: '1px 8px', background: 'none',
                          border: '1px dashed #c7d2fe', borderRadius: 4,
                          fontSize: 11, cursor: 'pointer', color: '#6366f1',
                        }}
                      >
                        + sos timing
                      </button>
                      {!timingOk && (
                        <span style={{ fontSize: 11, color: '#dc2626', marginLeft: 8 }}>
                          ⚠ select valid times to include in roster
                        </span>
                      )}
                    </div>

                    {/* Officer list */}
                    {group.expanded && (
                      <div style={{ padding: '6px 12px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {group.officers.map(officer => {
                          const dupCheck = dupCheckByOfficerId.get(officer.id);
                          const dupStatus = dupCheck?.status ?? 'new';
                          return (
                          <div key={officer.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '3px 4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              type="checkbox"
                              checked={officer.selected}
                              onChange={e => updateOfficer(group.id, officer.id, { selected: e.target.checked })}
                              style={{ width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }}
                            />
                            <input
                              type="text"
                              value={officer.name}
                              onChange={e => updateOfficer(group.id, officer.id, { name: e.target.value })}
                              placeholder="Officer name"
                              style={{
                                flex: 1, minWidth: 0, padding: '2px 6px',
                                border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13,
                                color: officer.selected ? '#1e293b' : '#94a3b8',
                                textDecoration: officer.selected ? 'none' : 'line-through',
                                background: officer.selected ? 'white' : '#f8fafc',
                              }}
                            />
                            {dupStatus === 'duplicate' && dupCheck && (
                              <span
                                title="An officer with this name is already deployed here for an overlapping window — any HHMM shown in red differs from what you just pasted"
                                style={{
                                  flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '2px 6px',
                                  borderRadius: 99, background: '#f1f5f9', color: '#94a3b8', whiteSpace: 'nowrap',
                                }}
                              >
                                ✓ already added for <WindowsWithDiff windows={dupCheck.existingWindows} />
                              </span>
                            )}
                            {dupStatus === 'extend' && dupCheck && (
                              <span
                                title="Same name and deployment already on file, but for a different time window — worth checking whether this is really a new booking before adding"
                                style={{
                                  flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '2px 6px',
                                  borderRadius: 99, background: '#dbeafe', color: '#2563eb', whiteSpace: 'nowrap',
                                }}
                              >
                                ↔ already added for <WindowsWithDiff windows={dupCheck.existingWindows} /> (different hours)
                              </span>
                            )}
                            {dupStatus === 'elsewhere' && (
                              <span
                                title="Same name already exists elsewhere in the roster (different deployment) — verify this isn't a duplicate before adding"
                                style={{
                                  flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '2px 6px',
                                  borderRadius: 99, background: '#fef3c7', color: '#b45309', whiteSpace: 'nowrap',
                                }}
                              >
                                ⚠ same name elsewhere
                              </span>
                            )}
                            <button
                              onClick={() => removeOfficer(group.id, officer.id)}
                              title="Remove officer"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}
                            >×</button>
                          </div>
                          </div>
                          );
                        })}
                        <button
                          onClick={() => addOfficerToGroup(group.id)}
                          style={{ alignSelf: 'flex-start', marginTop: 4, padding: '2px 8px', background: 'none', border: '1px dashed #c7d2fe', borderRadius: 4, fontSize: 12, cursor: 'pointer', color: '#6366f1' }}
                        >
                          + Officer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Full-width dotted "add another deployment" button — at the bottom so
              users don't need to scroll up. Only needed once groups already
              exist; the empty-state entry point is the Extract/OR/Manual row
              above instead. */}
          {sosGroups.length > 0 && (
            <button
              onClick={addEmptyGroup}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                width: '100%', padding: '10px', background: '#fafbff',
                border: '1.5px dashed #c7d2fe', borderRadius: 8,
                color: '#6366f1', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                marginBottom: 14,
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 300 }}>+</span>
              Add Deployment
            </button>
          )}

          {/* Step 3: Confirm — sticky footer, always visible (like the Main tab's
              Add button) rather than only appearing once groups exist, so the
              drawer's layout doesn't jump/shift as soon as something is
              extracted. Grayed out via Button's own disabled styling until
              there's actually something to add. */}
          <div style={{
            position: 'sticky', bottom: 0, background: 'white',
            borderTop: '1px solid #e2e8f0', paddingTop: 5, marginTop: 2,
            zIndex: 10,
          }}>
            {hasInvalidTiming && (
              <p style={{ fontSize: 12, color: '#dc2626', margin: '0 0 4px' }}>
                ⚠ Groups with incomplete timing will be skipped.
              </p>
            )}
            <Button
              onClick={handleSOSConfirm}
              disabled={totalSOSSelected === 0}
              loading={sosLoading}
              loadingText={`Adding ${totalSOSSelected} SOS officer${totalSOSSelected !== 1 ? 's' : ''}…`}
              style={{ width: '100%', justifyContent: 'center', padding: '7px 20px', ...CONFIRM_STYLE.sos }}
            >
              {`✅ Add ${totalSOSSelected} SOS Officer${totalSOSSelected !== 1 ? 's' : ''}${sosAutoAssign ? '' : ' (manual)'}`}
            </Button>
          </div>
        </div>
      )}

      {/* ── OT TAB ──────────────────────────────────────────────────────────── */}
      {subTab === 'ot' && (
        <div>
          {/* Step 1: Assignment mode selector */}
          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', marginBottom: 14 }}>
            <button
              onClick={() => setOTAutoAssign(true)}
              style={{
                flex: 1, padding: '9px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                background: otAutoAssign ? TAB_ACTIVE.ot.background : '#f8fafc',
                color: otAutoAssign ? TAB_ACTIVE.ot.color : '#64748b',
                fontWeight: otAutoAssign ? 700 : 400,
                borderRight: '1px solid #e2e8f0',
              }}
            >
              🤖 Auto-assign counters
            </button>
            <button
              onClick={() => setOTAutoAssign(false)}
              style={{
                flex: 1, padding: '9px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                background: !otAutoAssign ? '#f0fdf4' : '#f8fafc',
                color: !otAutoAssign ? '#166534' : '#64748b',
                fontWeight: !otAutoAssign ? 700 : 400,
              }}
            >
              ✋ I'll assign manually
            </button>
          </div>
          {!otAutoAssign && (
            <p style={{ fontSize: 12, color: '#166534', marginBottom: 12, marginTop: -8, padding: '5px 8px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
              Officers will be added with availability markers only. Drag on their row in the schedule grid to assign counters.
            </p>
          )}

          {/* Step 2: Configure timing */}
          {/* Preset quick-fill buttons — on the same line as the label; only
              wraps to a second line if the presets themselves don't fit. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: '#64748b', flexShrink: 0 }}>
              Quick-fill preset:
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {otPresets.map(p => {
                const key = p.label.split(' ')[0];
                const active = otPreset === key;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setOTPreset(key); setOTStartTime(p.start); setOTEndTime(p.end);
                      setOTStaysUntil('');
                      // Break sizes and start times fetched from backend via useEffect; reset state
                      setOTBreakSizes([null, null, null]);
                      setOTBreakModified(false);
                    }}
                    style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                      fontWeight: active ? 700 : 400, border: '1px solid',
                      background: active ? TAB_ACTIVE.ot.background : '#f1f5f9',
                      color: active ? TAB_ACTIVE.ot.color : '#475569',
                      borderColor: active ? TAB_ACTIVE.ot.borderColor : '#e2e8f0',
                      lineHeight: 1.5,
                    }}
                  >
                    {fmtTime(p.start)}–{fmtTime(p.end)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Form grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 }}>Start</div>
              <select
                value={otStartTime}
                onChange={e => {
                  const v = e.target.value;
                  setOTStartTime(v); setOTPreset('');
                  setOTBreakSizes([null, null, null]);
                  setOTBreakModified(false);
                }}
                style={{ width: '100%', padding: '6px', borderRadius: 6, border: otTimeValid ? '1px solid #ddd' : '1px solid #dc3545', fontSize: 13 }}
              >
                {TIME_SLOTS_15.map(t => <option key={t} value={t}>{fmtTime(t)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 }}>End</div>
              <select
                value={otEndTime}
                onChange={e => {
                  const v = e.target.value;
                  setOTEndTime(v); setOTPreset(''); setOTStaysUntil('');
                  setOTBreakSizes([null, null, null]);
                  setOTBreakModified(false);
                }}
                style={{ width: '100%', padding: '6px', borderRadius: 6, border: otTimeValid ? '1px solid #ddd' : '1px solid #dc3545', fontSize: 13 }}
              >
                {TIME_SLOTS_15.map(t => <option key={t} value={t}>{fmtTime(t)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 }}>No. of officers</div>
              <select
                value={otNumOfficers}
                onChange={e => setOTNumOfficers(Number(e.target.value))}
                style={{ width: '100%', padding: '6px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}
              >
                {Array.from({ length: 30 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {!otTimeValid && <p style={{ color: '#dc3545', fontSize: 12, marginBottom: 8 }}>End time must be after start time.</p>}

          {/* An OT window ending at the end of the shift: when the officer really leaves */}
          {otTimeValid && otBreakStarts?.hoto && (
            <OTHotoStrip
              hoto={otBreakStarts.hoto}
              staysUntil={otBreakStarts.stays_until}
              startHour={shiftStartHour}
              onChange={setOTStaysUntil}
            />
          )}

          {/* Break schedule: break sizes + break window in one yellow container (auto-assign only) */}
          {otAutoAssign && otTimeValid && (() => {
            const resetAll = () => {
              setOTBreakSizes(otBreakDefaults);
              setOTBreakModified(false);
              setOTBreakStartsChecked(defaultBreakStarts(otBreakStarts?.options));
              setOtAllow3hLastBreak(false);
            };
            return (
              <div style={{ marginBottom: 10, padding: '4px 10px 8px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Break schedule</span>
                  <button
                    onClick={resetAll}
                    title="Reset break plan and break times to defaults for this timing"
                    style={{ background: 'none', border: '1px solid #d97706', borderRadius: 4, cursor: 'pointer', color: '#d97706', fontSize: 12, padding: '1px 5px', lineHeight: 1.4, marginLeft: 'auto' }}
                  >↺</button>
                </div>
                {/* Break size dropdowns */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  {(['1st break', '2nd break', '3rd break'] as const).map((label, bIdx) => {
                    const slotsVal = otBreakSizes[bIdx];
                    const palette = slotsVal != null ? getDurationPalette(slotsVal * 15, 15) : null;
                    return (
                      <div key={bIdx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: 9, color: '#92400e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{label}</span>
                        <select
                          value={slotsVal ?? ''}
                          onChange={e => {
                            const val = e.target.value === '' ? null : parseInt(e.target.value);
                            setOTBreakSizes(prev => prev.map((s, i) => i === bIdx ? val : s));
                            setOTBreakModified(true);
                          }}
                          style={{
                            width: '100%', padding: '2px 1px',
                            border: palette ? 'none' : '1px solid #fde68a',
                            borderRadius: 4, fontSize: 10,
                            background: palette?.bg ?? 'white',
                            color: palette?.textColor ?? '#a16207',
                            fontWeight: palette ? 600 : 400,
                          }}
                        >
                          <option value="">None</option>
                          {BREAK_OPTIONS.map(opt => (
                            <option key={opt.slots} value={opt.slots}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
                {/* When the officer leaves, and the break start checklist */}
                {otBreakStarts && (
                  <OTBreakTimes
                    breakStarts={otBreakStarts}
                    checked={otBreakStartsChecked}
                    onToggle={t => setOTBreakStartsChecked(prev => {
                      const next = new Set(prev);
                      if (next.has(t)) next.delete(t); else next.add(t);
                      return next;
                    })}
                    lastBreakOn={otAllow3hLastBreak}
                    onLastBreakToggle={setOtAllow3hLastBreak}
                  />
                )}
              </div>
            );
          })()}

          <Button
            onClick={handleOTConfirm}
            disabled={!otTimeValid
              || (otAutoAssign && noBreakTimeTicked(otBreakStarts, otBreakStartsChecked, otAllow3hLastBreak))}
            loading={otLoading}
            loadingText={`Adding ${totalOTCount} OT officer${totalOTCount !== 1 ? 's' : ''}…`}
            style={{ width: '100%', justifyContent: 'center', ...CONFIRM_STYLE.ot }}
          >
            {`✅ Add ${totalOTCount} OT Officer${totalOTCount !== 1 ? 's' : ''}${otAutoAssign ? '' : ' (manual)'}`}
          </Button>
        </div>
      )}

      {/* ── MAIN TAB ────────────────────────────────────────────────────────── */}
      {subTab === 'main' && (
        <div>
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 14 }}>
            Add a main officer who was not in the initial roster. Their counter schedule is loaded automatically from the template.
          </p>

          {unusedSns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 14 }}>
              All serial numbers ({MAX_SN}) are already in use.
            </div>
          ) : (
            <>
              {/* SN + Name row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 }}>Serial Number</div>
                  <select
                    value={mainSN}
                    onChange={e => setMainSN(Number(e.target.value))}
                    style={{
                      width: '100%', padding: '8px', borderRadius: 6,
                      border: `1px solid ${mainSN ? '#d1d5db' : '#fca5a5'}`, fontSize: 14,
                    }}
                  >
                    <option value={0}>-- Select --</option>
                    {unusedSns.map(sn => <option key={sn} value={sn}>SN {sn}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 }}>
                    Name <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                  </div>
                  <input
                    type="text"
                    value={mainName}
                    onChange={e => setMainName(e.target.value)}
                    placeholder="Officer name"
                    style={{
                      width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd',
                      fontSize: 13, boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              {/* Availability window */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 }}>Availability window</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select
                    value={mainAvailStart}
                    onChange={e => setMainAvailStart(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, flex: 1 }}
                  >
                    {TIME_SLOTS_15.map(t => <option key={t} value={t}>{fmtTime(t)}</option>)}
                  </select>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>–</span>
                  <select
                    value={mainAvailEnd}
                    onChange={e => setMainAvailEnd(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, flex: 1 }}
                  >
                    {TIME_SLOTS_15.map(t => <option key={t} value={t}>{fmtTime(t)}</option>)}
                  </select>
                </div>
              </div>

              {/* Template preview */}
              {mainSN > 0 && (
                <div style={{ marginBottom: 14, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '6px 10px', background: '#f8fafc', fontSize: 12, fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>
                    Counter schedule for SN{mainSN}
                    {mainTemplateLoading && <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6 }}>loading…</span>}
                  </div>
                  {!mainTemplateLoading && mainTemplateRows.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>
                      No template found — officer will be added with an empty schedule.
                    </div>
                  )}
                  {!mainTemplateLoading && mainTemplateRows.length > 0 && trimmedMainRows.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>
                      No counters fall within the selected availability window.
                    </div>
                  )}
                  {trimmedMainRows.length > 0 && (
                    <div style={{ padding: '6px 8px', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {trimmedMainRows.map((r, i) => (
                        <span key={i} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#e0f2fe', color: '#0369a1', borderRadius: 12,
                          padding: '2px 8px', fontSize: 11, fontWeight: 600,
                        }}>
                          {formatCounterLabel(r.counter, mode)} <span style={{ fontWeight: 400, color: '#0ea5e9' }}>{fmtTime(r.start)}–{fmtTime(r.end)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={handleMainConfirm}
                disabled={!mainSN}
                loading={mainLoading}
                loadingText={`Adding main officer${mainSN ? ` SN${mainSN}` : ''}…`}
                style={{ width: '100%', justifyContent: 'center', ...CONFIRM_STYLE.main }}
              >
                {`✅ Add Main Officer${mainSN ? ` SN${mainSN}` : ''}`}
              </Button>
            </>
          )}
        </div>
      )}

      {(partialOfficers.length > 0 || failedOfficers.length > 0) && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '520px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>

            {/* Cancel the whole add — the way out when neither keeping nor dropping
                the partial officers is what the user wants. Sticky in a zero-height
                row so it stays reachable however far down a long list of flagged
                officers the dialog is scrolled. */}
            <div style={{ position: 'sticky', top: 0, height: 0, zIndex: 5, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => void handleCancelAdd()}
                disabled={cancellingAdd}
                title="Cancel — undo this whole add"
                aria-label="Cancel this add"
                style={{
                  background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '6px',
                  fontSize: '16px', lineHeight: 1, padding: '5px 9px', color: '#475569',
                  cursor: cancellingAdd ? 'wait' : 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '16px', paddingRight: '34px' }}>⚠️ Counter Assignment Issues</h3>

            {/* Group 1 — no counter at all: nothing to decide, they are left off the roster. */}
            {failedOfficers.length > 0 && (
              <>
                <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
                  <strong>{failedOfficers.length} officer(s)</strong> could not be assigned to any counter — no available counters during their entire shift. They have been excluded from the roster.
                </p>
                <div style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {failedOfficers.map((fo, i) => (
                    <div key={i} style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}>
                      <div style={{ fontWeight: 700 }}>{fo.name || fo.timing}{fo.deployment ? ` · ${fo.deployment}` : ''}</div>
                      <div style={{ color: '#555', marginTop: '2px' }}>Timing: {fo.timing}</div>
                      <div style={{ color: '#dc2626', marginTop: '2px', fontSize: '12px' }}>⛔ {fo.reason}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Group 2 — seated in part: keep (and pick breaks for the shorter window) or skip. */}
            {partialOfficers.length > 0 && (
              <>
                <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
                  <strong>{partialOfficers.length} officer(s)</strong> were only partially assigned — some time windows had no available counters. Keep each one for the time that can be covered, or skip them.
                </p>
                <div style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {partialOfficers.map(po => {
                    const shown = partialProposed.get(po.officer_key) ?? '';   // what they will hold, and what is sent
                    const canKeep = !!shown;
                    const keep = canKeep && partialKeep.get(po.officer_key) !== false;
                    const sizes = partialBreakSizes.get(po.officer_key) ?? [null, null, null];
                    return (
                      <div key={po.officer_key} style={{ background: keep ? '#fef3c7' : '#f1f5f9', border: `1px solid ${keep ? '#fde68a' : '#e2e8f0'}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ fontWeight: 700, flex: 1 }}>{po.name || po.officer_key}{po.deployment ? ` · ${po.deployment}` : ''}</div>
                          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                            {([true, false] as const).map(choice => (
                              <button
                                key={String(choice)}
                                disabled={choice && !canKeep}
                                onClick={() => setPartialKeep(prev => new Map(prev).set(po.officer_key, choice))}
                                style={{
                                  padding: '3px 10px', border: 'none', fontSize: '12px', fontWeight: 700,
                                  cursor: choice && !canKeep ? 'not-allowed' : 'pointer',
                                  background: keep === choice ? (choice ? '#16a34a' : '#ef4444') : 'white',
                                  color: keep === choice ? 'white' : '#475569',
                                }}
                              >
                                {choice ? 'Keep' : 'Skip'}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div style={{ color: '#555', marginTop: '2px' }}>
                          Timing: {keep ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#b45309' }}>{po.timing}</span>
                              {' → '}
                              <span style={{ color: '#166534', fontWeight: 600 }}>{shown}</span>
                            </>
                          ) : po.timing}
                        </div>
                        <div style={{ color: '#b45309', marginTop: '2px', fontSize: '12px' }}>⚠️ No counter: {po.unassigned_times}</div>

                        {keep && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#64748b', flexShrink: 0 }}>Breaks for {shown}:</span>
                              <button
                                onClick={() => void handleResetPartialBreakToDefault(po, shown)}
                                title="Recompute break sizes based on the trimmed timing"
                                style={{ marginLeft: 'auto', padding: '2px 8px', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', color: '#6b7280', cursor: 'pointer', fontSize: '10px' }}
                              >
                                ↺ Reset to default
                              </button>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'flex-end' }}>
                              {([0, 1, 2] as const).map(bIdx => (
                                <div key={bIdx} style={{ flex: 1 }}>
                                  <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '2px' }}>
                                    {bIdx === 0 ? '1st' : bIdx === 1 ? '2nd' : '3rd'}
                                  </div>
                                  <select
                                    value={sizes[bIdx] ?? ''}
                                    onChange={e => {
                                      const val = e.target.value === '' ? null : parseInt(e.target.value);
                                      setPartialBreakSizes(prev => {
                                        const next = new Map(prev);
                                        const cur = [...(next.get(po.officer_key) ?? [null, null, null])];
                                        cur[bIdx] = val;
                                        next.set(po.officer_key, cur as (number | null)[]);
                                        return next;
                                      });
                                    }}
                                    style={{ width: '100%', padding: '3px 2px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px' }}
                                  >
                                    <option value="">None</option>
                                    {BREAK_OPTIONS.map(opt => (
                                      <option key={opt.slots} value={opt.slots}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <Button
              onClick={() => void confirmPartialDecision()}
              loading={partialDeciding}
              loadingText={partialOfficers.length > 0 ? 'Re-assigning officers…' : 'Saving…'}
              style={{ width: '100%', padding: '10px', background: partialOfficers.length > 0 ? '#16a34a' : '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '14px' }}
            >
              {partialOfficers.length > 0 ? 'Confirm' : 'OK'}
            </Button>

          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
